import DOMPurify from 'dompurify';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import {
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';

import './variableTemplateEditor.css';

const TOKEN_PATTERN = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;
export const VARIABLE_DRAG_TYPE = 'text/x-avworkforce-variable';

const labelFor = (value: string) =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const createChip = (field: string) => {
  const chip = document.createElement('span');
  chip.className = 'variable-template-chip';
  chip.contentEditable = 'false';
  chip.dataset.variable = field;
  chip.setAttribute('aria-label', `${labelFor(field)} variable`);

  const label = document.createElement('span');
  label.textContent = labelFor(field);
  chip.appendChild(label);

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.dataset.removeVariable = field;
  remove.setAttribute('aria-label', `Remove ${labelFor(field)} variable`);
  remove.textContent = '×';
  chip.appendChild(remove);
  return chip;
};

const serialize = (root: HTMLElement): string => {
  let value = '';
  let previousWasBlock = false;
  Array.from(root.childNodes).forEach((node) => {
    const element = node instanceof HTMLElement ? node : null;
    const isBlock = Boolean(element && /^(DIV|P|LI|UL|OL|BLOCKQUOTE)$/.test(element.tagName));
    if ((isBlock || previousWasBlock) && value && !value.endsWith('\n')) value += '\n';
    if (node.nodeType === Node.TEXT_NODE) value += node.textContent ?? '';
    else if (element?.dataset.variable) value += `{{${element.dataset.variable}}}`;
    else if (element?.tagName === 'BR') value += '\n';
    else if (element) value += serialize(element);
    previousWasBlock = isBlock;
  });
  return value;
};

const renderValue = (root: HTMLElement, value: string) => {
  root.replaceChildren();
  let cursor = 0;
  for (const match of value.matchAll(TOKEN_PATTERN)) {
    const index = match.index ?? 0;
    if (index > cursor)
      root.appendChild(document.createTextNode(value.slice(cursor, index)));
    root.appendChild(createChip(match[1]));
    cursor = index + match[0].length;
  }
  if (cursor < value.length)
    root.appendChild(document.createTextNode(value.slice(cursor)));
};

// Replace tokens in text nodes only, preserving links, emphasis, lists and tables.
const renderRichValue = (root: HTMLElement, value: string) => {
  root.innerHTML = DOMPurify.sanitize(value, { FORBID_TAGS: ['style'] });
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  nodes.forEach((node) => {
    const fragment = document.createElement('span');
    renderValue(fragment, node.textContent || '');
    node.replaceWith(...Array.from(fragment.childNodes));
  });
};

const serializeRichValue = (root: HTMLElement, original: string) => {
  const clone = root.cloneNode(true) as HTMLElement;
  clone.querySelectorAll<HTMLElement>('[data-variable]').forEach((chip) => {
    chip.replaceWith(document.createTextNode(`{{${chip.dataset.variable}}}`));
  });
  if (/<html[\s>]/i.test(original)) {
    const doc = new DOMParser().parseFromString(original, 'text/html');
    doc.body.innerHTML = clone.innerHTML;
    return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
  }
  return clone.innerHTML;
};

const rangeBelongsTo = (range: Range | null, root: HTMLElement) =>
  Boolean(range && root.contains(range.commonAncestorContainer));

export type VariableTemplateEditorHandle = {
  insertVariable: (field: string) => void;
  focus: () => void;
};

type VariableTemplateEditorProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  multiline?: boolean;
  richHtml?: boolean;
  placeholder?: string;
};

const VariableTemplateEditorInner = (
  {
    id,
    label,
    value,
    onChange,
    onFocus,
    multiline = false,
    richHtml = false,
    placeholder
  }: VariableTemplateEditorProps,
  forwardedRef: ForwardedRef<VariableTemplateEditorHandle>
) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const rangeRef = useRef<Range | null>(null);
  const lastValueRef = useRef<string | null>(null);

  const rememberSelection = () => {
    const root = rootRef.current;
    const selection = window.getSelection();
    if (!root || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (rangeBelongsTo(range, root)) rangeRef.current = range.cloneRange();
  };

  const emit = () => {
    const root = rootRef.current;
    if (root) {
      const next = richHtml ? serializeRichValue(root, value) : serialize(root);
      lastValueRef.current = next;
      onChange(next);
    }
  };

  const insertNode = (node: Node, requestedRange?: Range | null) => {
    const root = rootRef.current;
    if (!root) return;
    const range = rangeBelongsTo(requestedRange ?? rangeRef.current, root)
      ? (requestedRange ?? rangeRef.current)!.cloneRange()
      : document.createRange();
    if (!rangeBelongsTo(range, root)) {
      range.selectNodeContents(root);
      range.collapse(false);
    }
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    rangeRef.current = range.cloneRange();
    root.focus();
    emit();
  };

  useImperativeHandle(forwardedRef, () => ({
    insertVariable: (field) => insertNode(createChip(field)),
    focus: () => rootRef.current?.focus()
  }));

  useEffect(() => {
    const root = rootRef.current;
    if (!root || lastValueRef.current === value) return;
    if (richHtml) renderRichValue(root, value);
    else renderValue(root, value);
    lastValueRef.current = value;
    rangeRef.current = null;
  }, [value, richHtml]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (multiline)
        insertNode(
          richHtml
            ? document.createElement('br')
            : document.createTextNode('\n')
        );
    }
  };

  return (
    <label className="variable-template-field" htmlFor={id}>
      <span>{label}</span>
      <div
        id={id}
        ref={rootRef}
        className={`variable-template-editor ${multiline ? 'multiline' : ''}`}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label={label}
        aria-multiline={multiline}
        data-placeholder={placeholder}
        onFocus={() => {
          onFocus?.();
          rememberSelection();
        }}
        onInput={() => {
          rememberSelection();
          emit();
        }}
        onKeyDown={handleKeyDown}
        onKeyUp={rememberSelection}
        onMouseUp={rememberSelection}
        onPaste={(event) => {
          event.preventDefault();
          insertNode(
            document.createTextNode(event.clipboardData.getData('text/plain'))
          );
        }}
        onClick={(event) => {
          const target = event.target as HTMLElement;
          const remove = target.closest<HTMLButtonElement>(
            '[data-remove-variable]'
          );
          if (!remove) return;
          event.preventDefault();
          remove.closest<HTMLElement>('[data-variable]')?.remove();
          emit();
        }}
        onDragOver={(event) => {
          if (event.dataTransfer.types.includes(VARIABLE_DRAG_TYPE)) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
          }
        }}
        onDrop={(event) => {
          const field = event.dataTransfer.getData(VARIABLE_DRAG_TYPE);
          if (!field) return;
          event.preventDefault();
          const documentWithCaret = document as Document & {
            caretRangeFromPoint?: (x: number, y: number) => Range | null;
          };
          insertNode(
            createChip(field),
            documentWithCaret.caretRangeFromPoint?.(
              event.clientX,
              event.clientY
            )
          );
        }}
      />
    </label>
  );
};

export const VariableTemplateEditor = forwardRef(VariableTemplateEditorInner);

export const VariableFieldPalette = ({
  fields,
  onInsert
}: {
  fields: string[];
  onInsert: (field: string) => void;
}) => (
  <div className="variable-template-palette">
    {fields.map((field) => (
      <button
        key={field}
        type="button"
        draggable
        onDragStart={(event) => {
          event.dataTransfer.setData(VARIABLE_DRAG_TYPE, field);
          event.dataTransfer.effectAllowed = 'copy';
        }}
        onClick={() => onInsert(field)}
      >
        <AddRoundedIcon aria-hidden="true" fontSize="small" />
        <span>{labelFor(field)}</span>
      </button>
    ))}
  </div>
);
